import {useEffect} from 'react';

import {LivePreviewServiceInstance} from 'Application/services/LivePreviewService';

interface Args<T extends Record<string, any>> {
    dataKey: string;
    depth: number;
    initialData: T;
}

/**
 * A React hook that integrates a component with the live preview service and returns reactive preview data.
 * This hook subscribes the component to live preview updates using a unique `dataKey`, initial data, and server configuration.
 * It ensures that subscription is established on mount and automatically cleaned up on unmount via `useEffect`.
 * The hook returns the reactive data object for the given key, which updates automatically as new data is received from the server.
 *
 * @param props             The properties object containing the necessary parameters for the live preview.
 * @param props.dataKey     A unique key used to store and identify the previewed data.
 * @param props.depth       The fetch depth for related fields from the preview server.
 * @param props.initialData The initial data to populate the preview state with.
 * @returns The latest merged preview data of type `T`, kept in sync via the live preview service.
 *
 * @example
 * ```tsx
 * const page = useLivePreview<Page>(initialPageData, 2, 'pagePreview', 'https://preview.example.com');
 * ```
 */
export const useLivePreview = <T extends Record<string, any>>({dataKey, depth, initialData}: Args<T>) => {
    useEffect(() => {
        let host = '';
        let preview = false;

        if (typeof window !== 'undefined') {
            const query = new URLSearchParams(window.location.search);

            host = query.get('host') ?? window.location.host;
            preview = query.has('preview');
        }

        if (preview) {
            LivePreviewServiceInstance.subcribe(initialData, depth, dataKey, `https://${host}`);
        }

        return () => LivePreviewServiceInstance.unsubscribe(dataKey);
    }, [dataKey, depth, initialData]);

    const previewData = LivePreviewServiceInstance.previewData[dataKey] as T | undefined;

    return previewData ?? initialData;
};